import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { allObjectFields, getPicklistValues, getStructuredPicklistValues } from '../mockData/objectManagerFields';
import { createPersistedMockData } from '../mockDataManager';
import {
  createGobodoField,
  getGobodoFields,
  updateGobodoField,
  deleteGobodoField,
  resolveObjectKey
} from '../gobodo/gobodoObjectService';
import { CustomObject, findCachedObjectById } from './objectQueries';
import { getCustomSuffix } from '../../config/gobodoConfig';

import { USE_GOBODO_API } from '../../utils/gobodoUtils';

// ============================================
// TYPES
// ============================================

/**
 * PE-1470: Structured picklist value with optional metadata
 * Enables auto-mapping between fields (e.g., Stage → Probability)
 */
export interface PicklistValue {
  value: string;           // The actual value stored
  label?: string;          // Display label (defaults to value if not set)
  metadata?: Record<string, any>;  // Custom metadata (e.g., { defaultProbability: 20 })
}

/**
 * Helper to normalize picklist values to structured format
 * Supports both legacy string[] and new PicklistValue[] formats
 */
export function normalizePicklistValues(values: (string | PicklistValue)[] | undefined): PicklistValue[] {
  if (!values) return [];
  return values.map(v => {
    if (typeof v === 'string') {
      return { value: v, label: v };
    }
    return v;
  });
}

/**
 * Helper to get simple string array from picklist values (for backward compatibility)
 */
export function getPicklistValuesAsStrings(values: (string | PicklistValue)[] | undefined): string[] {
  if (!values) return [];
  return values.map(v => typeof v === 'string' ? v : v.value);
}

export interface CustomField {
  id: string;
  objectId: string;
  fieldName: string; // API name (e.g., contract_value__c)
  fieldLabel: string; // Display name
  fieldType:
    | 'text'
    | 'textarea'
    | 'longtextarea'
    | 'richtextarea'
    | 'number'
    | 'currency'
    | 'percent'
    | 'autonumber'
    | 'date'
    | 'datetime'
    | 'time'
    | 'checkbox'
    | 'badge'
    | 'picklist'
    | 'multipicklist'
    | 'lookup'
    | 'masterdetail'
    | 'formula'
    | 'rollup'
    | 'email'
    | 'phone'
    | 'url'
    | 'geolocation'
    | 'file'
    | 'image';
  description?: string;
  helpText?: string;
  required: boolean;
  unique: boolean;
  defaultValue?: string;
  length?: number; // For text fields
  precision?: number; // For number fields
  scale?: number; // For number fields
  picklistValues?: (string | PicklistValue)[]; // For picklist fields - supports both formats
  lookupObject?: string; // For lookup fields
  relTypeId?: number; // For lookup/masterdetail fields - relationship type ID from Gobodo

  // Auto-number settings
  autoNumberFormat?: string;  // e.g., "INV-{0000}"
  autoNumberStarting?: number;

  // Formula settings
  formulaReturnType?: 'text' | 'number' | 'currency' | 'date' | 'checkbox' | 'percent';
  formulaExpression?: string;

  // Roll-up settings
  rollupChildObject?: string;
  rollupChildField?: string;
  rollupOperation?: 'SUM' | 'COUNT' | 'MIN' | 'MAX' | 'AVG';
  rollupFilter?: string;

  // Master-Detail settings
  masterDetailCascadeDelete?: boolean;
  masterDetailShareAccess?: 'ReadOnly' | 'ReadWrite';

  isPII: boolean;
  isStandard: boolean;
  isActive: boolean;
  isSystemField?: boolean;  // True for system fields (id, createdDate, etc.)
  isReadOnly?: boolean;     // True if field can't be edited by users
  fieldLevelSecurity: {
    visibleTo: string[]; // Role IDs
    editableBy: string[]; // Role IDs
  };
  createdBy: string;
  createdDate: string;
  lastModified: string;
}

// ============================================
// MOCK DATA
// ============================================

// Convert nested field structure to flat array for persistence
const defaultFields: CustomField[] = Object.entries(allObjectFields).flatMap(([objectId, fields]) => fields);

// Create persisted storage using mockDataManager
export const persistedObjectFields = createPersistedMockData<CustomField>(
  'crm_object_fields',
  defaultFields
);

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get fields for a specific object from persisted storage
 * Falls back to default fields if not found in localStorage (handles new object types)
 */
function getFieldsByObjectId(objectId: string): CustomField[] {
  console.log('[getFieldsByObjectId] ===== FETCHING FIELDS =====');
  console.log('[getFieldsByObjectId] Object ID:', objectId);

  const allFields = persistedObjectFields.get();
  let filtered = allFields.filter(f => f.objectId === objectId);

  console.log('[getFieldsByObjectId] Total fields in storage:', allFields.length);
  console.log('[getFieldsByObjectId] Fields for this object:', filtered.length);

  // If no fields found in localStorage, fall back to default fields from allObjectFields
  // This handles newly added objects that weren't in the original localStorage data
  if (filtered.length === 0 && allObjectFields[objectId]) {
    console.log('[getFieldsByObjectId] No fields in storage, falling back to defaults for:', objectId);
    filtered = allObjectFields[objectId];
    console.log('[getFieldsByObjectId] Default fields found:', filtered.length);
  }

  // DEBUG: Log picklist fields to see their state
  const picklistFields = filtered.filter(f =>
    f.fieldType === 'picklist' ||
    f.fieldType === 'multipicklist' ||
    f.fieldName.toLowerCase().includes('stage') ||
    f.fieldName.toLowerCase().includes('status')
  );

  console.log('[getFieldsByObjectId] Picklist fields found:', picklistFields.length);
  picklistFields.forEach(f => {
    console.log('[getFieldsByObjectId] Picklist field:', {
      id: f.id,
      fieldName: f.fieldName,
      fieldType: f.fieldType,
      picklistValuesCount: f.picklistValues?.length || 0,
      firstValue: f.picklistValues?.[0],
      hasMetadata: typeof f.picklistValues?.[0] === 'object' && 'metadata' in (f.picklistValues[0] as any || {})
    });
  });

  return filtered;
}

/**
 * PE-1470: Merge localStorage picklist values with API fields.
 * When using Gobodo API, picklist values with metadata are saved to localStorage
 * but the API might not return them. This function merges them back.
 */
function mergeLocalStoragePicklistValues(apiFields: CustomField[], objectId: string): CustomField[] {
  // Get fields from localStorage
  const localFields = persistedObjectFields.get().filter(f => f.objectId === objectId);

  // Create a map of fieldId/fieldName -> picklistValues from localStorage
  const localPicklistMap = new Map<string, (string | PicklistValue)[]>();
  localFields.forEach(f => {
    if (f.picklistValues && f.picklistValues.length > 0) {
      localPicklistMap.set(f.id, f.picklistValues);
      localPicklistMap.set(f.fieldName, f.picklistValues);
    }
  });

  console.log('[mergeLocalStoragePicklistValues] Local picklist map:', {
    objectId,
    localFieldsCount: localFields.length,
    fieldsWithPicklistValues: localPicklistMap.size / 2 // Divide by 2 since we store both id and fieldName
  });

  // Merge localStorage picklist values into API fields
  return apiFields.map(field => {
    const localValues = localPicklistMap.get(field.id) || localPicklistMap.get(field.fieldName);
    if (localValues && localValues.length > 0) {
      console.log('[mergeLocalStoragePicklistValues] Merging picklist values for:', {
        fieldId: field.id,
        fieldName: field.fieldName,
        apiValuesCount: field.picklistValues?.length || 0,
        localValuesCount: localValues.length
      });
      return { ...field, picklistValues: localValues };
    }
    return field;
  });
}

/**
 * Enrich fields with known picklist values when API returns text or empty options.
 * Ensures Status, Priority, Type etc. render as selects in Task/Case/Client modals.
 *
 * IMPORTANT: This function should NOT overwrite user-saved picklist values.
 * It only enriches fields that:
 * 1. Are not already marked as picklist type, OR
 * 2. Are picklist type but have no picklistValues defined
 */
function enrichKnownPicklists(fields: CustomField[], objectId: string): CustomField[] {
  const normalized = (name: string) => name.replace(/__c$/i, '').toLowerCase();
  return fields.map(field => {
    // First try to get structured values with metadata (PE-1470)
    const structuredValues: (string | PicklistValue)[] | undefined =
      getStructuredPicklistValues(field.fieldName, objectId) ??
      getStructuredPicklistValues(normalized(field.fieldName), objectId);

    // Fall back to simple string values if no structured values exist
    const defaultValues: (string | PicklistValue)[] | undefined =
      structuredValues ??
      getPicklistValues(field.fieldName, objectId) ??
      getPicklistValues(normalized(field.fieldName), objectId);

    // Check if field already has user-defined picklist values
    // This handles both string[] and PicklistValue[] formats
    const hasExistingValues = Array.isArray(field.picklistValues) && field.picklistValues.length > 0;
    const isAlreadyPicklist = field.fieldType === 'picklist' || field.fieldType === 'multipicklist';

    // Only enrich if:
    // 1. We have default values to add, AND
    // 2. Either the field is not a picklist, OR it's a picklist with no values
    const shouldEnrich = defaultValues?.length && (!isAlreadyPicklist || !hasExistingValues);

    // DEBUG: Log picklist enrichment decisions for relevant fields
    if (isAlreadyPicklist || field.fieldName.toLowerCase().includes('stage') || field.fieldName.toLowerCase().includes('status')) {
      console.log('[enrichKnownPicklists]', {
        fieldName: field.fieldName,
        fieldType: field.fieldType,
        isAlreadyPicklist,
        hasExistingValues,
        existingCount: field.picklistValues?.length || 0,
        hasDefaultValues: !!defaultValues?.length,
        shouldEnrich,
        // Show first value to help debug format issues
        firstExistingValue: field.picklistValues?.[0],
        firstDefaultValue: defaultValues?.[0]
      });
    }

    if (shouldEnrich) {
      console.log('[enrichKnownPicklists] ENRICHING field:', field.fieldName, 'with', defaultValues?.length, 'values:', defaultValues);
      const enrichedField = { ...field, fieldType: 'picklist' as const, picklistValues: defaultValues };
      console.log('[enrichKnownPicklists] ENRICHED RESULT:', enrichedField.fieldName, 'picklistValues:', enrichedField.picklistValues);
      return enrichedField;
    }
    return field;
  });
}

// ============================================
// QUERY KEYS
// ============================================

export const fieldKeys = {
  all: ['fields'] as const,
  byObject: (objectId: string) => [...fieldKeys.all, 'object', objectId] as const,
  detail: (fieldId: string) => [...fieldKeys.all, 'detail', fieldId] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all fields for an object
 * Future: Replace with API call to GET /api/objects/:objectId/fields
 */
export function useFieldsByObject(objectId: string, filters?: {
  includeInactive?: boolean;
  typeFilter?: string;
  searchQuery?: string;
}) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: [...fieldKeys.byObject(objectId), JSON.stringify(filters || {})],
    queryFn: async () => {
      if (USE_GOBODO_API) {
        // Reuse the object metadata already cached by Object Manager/Object Detail.
        // In particular, the numeric Gobodo object type id lets us skip a second
        // getObjectTypeByKey(...) call before fetching fields.
        const cachedObject: CustomObject | undefined = findCachedObjectById(queryClient, objectId);
        
        const exactKey = cachedObject?.apiName;
        const objectTypeId = cachedObject?.gobodoTypeId;
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('[useFieldsByObject] Resolving key:', {
            objectId,
            foundInCache: !!cachedObject,
            cachedObject: cachedObject ? { id: cachedObject.id, apiName: cachedObject.apiName } : null,
            exactKey,
            resolvedKey: resolveObjectKey(objectId, exactKey)
          });
        }
        
        // Convert objectId to Gobodo key using exact apiName if available
        const objectKey = resolveObjectKey(objectId, exactKey);

        // Product is sourced from Stripe, not the graph — skip Gobodo fields API to avoid getObjectTypeByKey('Product__c')
        // Invoice is sourced from listAllStripeInvoices — skip Gobodo fields API to avoid querying invoice_c
        const keyNorm = objectKey.replace(/\s/g, '').toLowerCase();
        if (keyNorm === 'product__c' || keyNorm === 'product') {
          await new Promise(resolve => setTimeout(resolve, 100));
          let fields = getFieldsByObjectId(objectId);
          if (!filters?.includeInactive) fields = fields.filter(f => f.isActive);
          if (filters?.typeFilter && filters.typeFilter !== 'all') {
            fields = fields.filter(f => f.fieldType === filters.typeFilter);
          }
          if (filters?.searchQuery) {
            const q = filters.searchQuery.toLowerCase();
            fields = fields.filter(f =>
              f.fieldLabel.toLowerCase().includes(q) ||
              f.fieldName.toLowerCase().includes(q) ||
              (f.description?.toLowerCase() ?? '').includes(q)
            );
          }
          fields = enrichKnownPicklists(fields, objectId);
          return fields.sort((a, b) => {
            if (a.isStandard && !b.isStandard) return -1;
            if (!a.isStandard && b.isStandard) return 1;
            return a.fieldLabel.localeCompare(b.fieldLabel);
          });
        }
        if (keyNorm === 'invoice__c' || keyNorm === 'invoice') {
          await new Promise(resolve => setTimeout(resolve, 100));
          let fields = getFieldsByObjectId(objectId);
          if (!filters?.includeInactive) fields = fields.filter(f => f.isActive);
          if (filters?.typeFilter && filters.typeFilter !== 'all') {
            fields = fields.filter(f => f.fieldType === filters.typeFilter);
          }
          if (filters?.searchQuery) {
            const q = filters.searchQuery.toLowerCase();
            fields = fields.filter(f =>
              f.fieldLabel.toLowerCase().includes(q) ||
              f.fieldName.toLowerCase().includes(q) ||
              (f.description?.toLowerCase() ?? '').includes(q)
            );
          }
          fields = enrichKnownPicklists(fields, objectId);
          return fields.sort((a, b) => {
            if (a.isStandard && !b.isStandard) return -1;
            if (!a.isStandard && b.isStandard) return 1;
            return a.fieldLabel.localeCompare(b.fieldLabel);
          });
        }

        try {
          // Fetch all pages of fields (for backward compatibility with useQuery)
          let allFields: CustomField[] = [];
          let page = 0;
          const pageSize = 200; // Was 10 — caused 11+ sequential requests for 107 fields
          let hasMore = true;

          while (hasMore) {
            const result = await getGobodoFields(objectKey, { page, size: pageSize, objectTypeId });
            allFields = allFields.concat(result.fields);
            hasMore = result.hasMore;
            page++;
          }

          // Apply filters
          if (!filters?.includeInactive) {
            allFields = allFields.filter((f: CustomField) => f.isActive);
          }

          if (filters?.typeFilter && filters.typeFilter !== 'all') {
            allFields = allFields.filter((f: CustomField) => f.fieldType === filters.typeFilter);
          }

          if (filters?.searchQuery) {
            const query = filters.searchQuery.toLowerCase();
            allFields = allFields.filter((f: CustomField) =>
              f.fieldLabel.toLowerCase().includes(query) ||
              f.fieldName.toLowerCase().includes(query) ||
              f.description?.toLowerCase().includes(query)
            );
          }

          // PE-1470: Merge localStorage picklist values (with metadata) into Gobodo API fields
          allFields = mergeLocalStoragePicklistValues(allFields, objectId);
          allFields = enrichKnownPicklists(allFields, objectId);
          return allFields.sort((a: CustomField, b: CustomField) => {
            if (a.isStandard && !b.isStandard) return -1;
            if (!a.isStandard && b.isStandard) return 1;
            return a.fieldLabel.localeCompare(b.fieldLabel);
          });
        } catch (err: any) {
          // 404 = object type has no fields in Gobodo; fall back to mock data
          if (err?.status === 404 || err?.response?.statusCode === 404) {
            console.warn(`[useFieldsByObject] Object ${objectKey} not found in Gobodo (404). Falling back to mock data.`);
            // Fall through to mock data path below
          } else {
            // Other errors (500, network) -- re-throw so React Query retries
            throw err;
          }
        }
      }

      // Mock data path (also used as fallback when Gobodo returns 404)
      await new Promise(resolve => setTimeout(resolve, USE_GOBODO_API ? 50 : 400));

      let fields = getFieldsByObjectId(objectId);

      if (!filters?.includeInactive) {
        fields = fields.filter(f => f.isActive);
      }

      if (filters?.typeFilter && filters.typeFilter !== 'all') {
        fields = fields.filter(f => f.fieldType === filters.typeFilter);
      }

      if (filters?.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        fields = fields.filter(f =>
          f.fieldLabel.toLowerCase().includes(query) ||
          f.fieldName.toLowerCase().includes(query) ||
          f.description?.toLowerCase().includes(query)
        );
      }
      fields = enrichKnownPicklists(fields, objectId);
      return fields.sort((a, b) => {
        if (a.isStandard && !b.isStandard) return -1;
        if (!a.isStandard && b.isStandard) return 1;
        return a.fieldLabel.localeCompare(b.fieldLabel);
      });
    },
    staleTime: 3 * 60 * 1000,
    enabled: !!objectId,
  });
}

/**
 * Get fields for an object using pagination
 * Supports page and pageSize parameters for traditional pagination
 */
export function useFieldsByObjectPaginated(
  objectId: string,
  options?: {
    page?: number; // 0-indexed page number
    pageSize?: number;
    filters?: {
      includeInactive?: boolean;
      typeFilter?: string;
      searchQuery?: string;
    };
  }
) {
  const queryClient = useQueryClient();
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? 10;
  const filters = options?.filters;
  
  return useQuery({
    queryKey: [...fieldKeys.byObject(objectId), 'paginated', page, pageSize, JSON.stringify(filters || {})],
    queryFn: async () => {
      if (USE_GOBODO_API) {
        // Try to get the exact apiName from the objects list cache
        const cachedObject: CustomObject | undefined = findCachedObjectById(queryClient, objectId);
        
        const exactKey = cachedObject?.apiName;
        const objectKey = resolveObjectKey(objectId, exactKey);

        // Product is sourced from Stripe, not the graph — use mock fields to avoid getObjectTypeByKey('Product__c')
        const keyNorm = objectKey.replace(/\s/g, '').toLowerCase();
        if (keyNorm === 'product__c' || keyNorm === 'product') {
          await new Promise(resolve => setTimeout(resolve, 100));
          let fields = getFieldsByObjectId(objectId);
          if (!filters?.includeInactive) fields = fields.filter(f => f.isActive);
          if (filters?.typeFilter && filters.typeFilter !== 'all') {
            fields = fields.filter(f => f.fieldType === filters.typeFilter);
          }
          if (filters?.searchQuery) {
            const q = filters.searchQuery.toLowerCase();
            fields = fields.filter(f =>
              f.fieldLabel.toLowerCase().includes(q) ||
              f.fieldName.toLowerCase().includes(q) ||
              (f.description?.toLowerCase() ?? '').includes(q)
            );
          }
          fields = enrichKnownPicklists(fields, objectId);
          const sorted = fields.sort((a, b) => {
            if (a.isStandard && !b.isStandard) return -1;
            if (!a.isStandard && b.isStandard) return 1;
            return a.fieldLabel.localeCompare(b.fieldLabel);
          });
          const start = page * pageSize;
          const paginated = sorted.slice(start, start + pageSize);
          return {
            fields: paginated,
            hasMore: start + paginated.length < sorted.length,
            page,
            size: pageSize,
            totalCount: sorted.length
          };
        }
        
        // Use fieldCount from object if available, otherwise estimate
        const totalFieldCount = cachedObject?.fieldCount;
        
        // Fetch paginated fields (API uses 0-indexed pages)
        let result;
        try {
          result = await getGobodoFields(objectKey, {
            page,
            size: pageSize
          });
        } catch (error: any) {
          console.error('[useFieldsByObjectPaginated] Error fetching fields:', error);
          console.error('[useFieldsByObjectPaginated] Error details:', {
            objectKey,
            objectId,
            page,
            pageSize,
            errorMessage: error?.message,
            errorStack: error?.stack
          });
          throw error; // Re-throw to let React Query handle it
        }
        
        // Apply client-side filters
        let filteredFields = result.fields;
        
        if (!filters?.includeInactive) {
          filteredFields = filteredFields.filter((f: CustomField) => f.isActive);
        }
        
        if (filters?.typeFilter && filters.typeFilter !== 'all') {
          filteredFields = filteredFields.filter((f: CustomField) => f.fieldType === filters.typeFilter);
        }
        
        if (filters?.searchQuery) {
          const query = filters.searchQuery.toLowerCase();
          filteredFields = filteredFields.filter((f: CustomField) =>
            f.fieldLabel.toLowerCase().includes(query) ||
            f.fieldName.toLowerCase().includes(query) ||
            f.description?.toLowerCase().includes(query)
          );
        }

        // PE-1470: Merge localStorage picklist values (with metadata) into Gobodo API fields
        filteredFields = mergeLocalStoragePicklistValues(filteredFields, objectId);
        // Also enrich with default picklist values if none exist
        filteredFields = enrichKnownPicklists(filteredFields, objectId);

        // Sort: standard fields first, then custom fields alphabetically
        const sortedFields = filteredFields.sort((a: CustomField, b: CustomField) => {
          if (a.isStandard && !b.isStandard) return -1;
          if (!a.isStandard && b.isStandard) return 1;
          return a.fieldLabel.localeCompare(b.fieldLabel);
        });
        
        // Calculate total count:
        // - If we have fieldCount from object, use it (but note: client-side filtering may reduce visible count)
        // - Otherwise estimate based on pagination
        let totalCount: number;
        if (totalFieldCount !== undefined && totalFieldCount !== null) {
          // Use object's fieldCount as base, but note that client-side filtering may affect actual visible count
          // For now, we'll use it as-is since we can't know the filtered count without fetching all fields
          totalCount = totalFieldCount;
        } else {
          // Estimate: if hasMore, we know there are at least (page + 1) * size fields
          // Otherwise, we have exactly (page * size) + fields.length
          totalCount = result.hasMore ? (page + 1) * pageSize + 1 : (page * pageSize) + sortedFields.length;
        }
        
        return {
          fields: sortedFields,
          hasMore: result.hasMore,
          page: result.page,
          size: result.size,
          totalCount
        };
      } else {
        await new Promise(resolve => setTimeout(resolve, 400));

        let fields = getFieldsByObjectId(objectId);

        if (!filters?.includeInactive) {
          fields = fields.filter((f: CustomField) => f.isActive);
        }

        if (filters?.typeFilter && filters.typeFilter !== 'all') {
          fields = fields.filter((f: CustomField) => f.fieldType === filters.typeFilter);
        }

        if (filters?.searchQuery) {
          const query = filters.searchQuery.toLowerCase();
          fields = fields.filter((f: CustomField) =>
            f.fieldLabel.toLowerCase().includes(query) ||
            f.fieldName.toLowerCase().includes(query) ||
            f.description?.toLowerCase().includes(query)
          );
        }

        const sortedFields = fields.sort((a: CustomField, b: CustomField) => {
          if (a.isStandard && !b.isStandard) return -1;
          if (!a.isStandard && b.isStandard) return 1;
          return a.fieldLabel.localeCompare(b.fieldLabel);
        });
        
        // Apply pagination to mock data
        const startIndex = page * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedFields = sortedFields.slice(startIndex, endIndex);
        
        return {
          fields: paginatedFields,
          hasMore: endIndex < sortedFields.length,
          page,
          size: pageSize,
          totalCount: sortedFields.length
        };
      }
    },
    staleTime: 3 * 60 * 1000,
    enabled: !!objectId,
  });
}

/**
 * Get single field
 * Future: Replace with API call to GET /api/fields/:fieldId
 */
export function useField(fieldId: string) {
  return useQuery({
    queryKey: fieldKeys.detail(fieldId),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));

      // Get persisted fields and find field by ID
      const allFields = persistedObjectFields.get();
      const field = allFields.find(f => f.id === fieldId);

      if (!field) {
        throw new Error('Field not found');
      }

      return field;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!fieldId,
  });
}

/**
 * Create new field
 * Future: Replace with API call to POST /api/objects/:objectId/fields
 */
export function useCreateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fieldData: Omit<CustomField, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'isStandard'>) => {
      // Validate lookup fields: ensure lookupObject is provided
      if (fieldData.fieldType === 'lookup' && !fieldData.lookupObject) {
        throw new Error('Related object is required for lookup fields. Please select a target object.');
      }
      
      if (USE_GOBODO_API) {
        // Try to get the exact apiName from the objects list cache
        // Search through all cached object list queries (they may have different filter keys)
        const cachedObject: CustomObject | undefined = findCachedObjectById(queryClient, fieldData.objectId);
        
        const exactKey = cachedObject?.apiName;
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('[useCreateField] Resolving key:', {
            objectId: fieldData.objectId,
            foundInCache: !!cachedObject,
            cachedObject: cachedObject ? { id: cachedObject.id, apiName: cachedObject.apiName } : null,
            exactKey,
            resolvedKey: resolveObjectKey(fieldData.objectId, exactKey)
          });
        }
        
        // Convert objectId to Gobodo key using exact apiName if available
        const objectKey = resolveObjectKey(fieldData.objectId, exactKey);
        
        // Ensure fieldName has custom suffix
        const suffix = getCustomSuffix();
        const fieldNameWithSuffix = fieldData.fieldName.endsWith(suffix)
          ? fieldData.fieldName
          : `${fieldData.fieldName}${suffix}`;
        
        const fieldDataWithSuffix = {
          ...fieldData,
          fieldName: fieldNameWithSuffix
        };
        
        return createGobodoField(objectKey, fieldDataWithSuffix);
      } else {
        // Fallback to mock data
        await new Promise(resolve => setTimeout(resolve, 600));

        const suffix = getCustomSuffix();
        const newField: CustomField = {
          ...fieldData,
          id: `field_${Date.now()}`,
          isStandard: false,
          createdBy: 'Current User',
          createdDate: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          // Add custom suffix to custom field names if not present
          fieldName: fieldData.fieldName.endsWith(suffix)
            ? fieldData.fieldName
            : `${fieldData.fieldName}${suffix}`
        };

        // Add to persisted storage using mockDataManager
        persistedObjectFields.add(newField);

        return newField;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: fieldKeys.byObject(data.objectId) });
      queryClient.invalidateQueries({ queryKey: ['objects', 'detail', data.objectId] });
      queryClient.invalidateQueries({ queryKey: ['setup', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      queryClient.refetchQueries({ queryKey: fieldKeys.byObject(data.objectId) });
    },
  });
}

/**
 * Update field
 * Future: Replace with API call to PATCH /api/fields/:fieldId
 */
export function useUpdateField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fieldId, updates, objectId }: { fieldId: string; updates: Partial<CustomField>; objectId?: string }) => {
      if (updates.fieldType === 'lookup' && !updates.lookupObject) {
        throw new Error('Related object is required for lookup fields. Please select a target object.');
      }
      
      if (USE_GOBODO_API) {
        const numericId = parseInt(fieldId.replace('field_', ''));
        if (isNaN(numericId)) {
          throw new Error('Invalid field ID format');
        }
        const resolvedObjectId = objectId ?? updates.objectId;

        // PE-1470: Also update localStorage for picklist values with metadata
        // Gobodo API may not fully support structured picklist metadata
        if (updates.picklistValues) {
          console.log('[useUpdateField] Gobodo API mode - also saving picklistValues to localStorage:', {
            fieldId,
            picklistValuesCount: updates.picklistValues.length
          });
          persistedObjectFields.update(fieldId, {
            picklistValues: updates.picklistValues,
            lastModified: new Date().toISOString()
          });
        }

        return updateGobodoField(numericId, updates, resolvedObjectId);
      } else {
        // Fallback to mock data
        await new Promise(resolve => setTimeout(resolve, 500));

        // DEBUG: Log what we're about to update
        console.log('[useUpdateField] Updating field:', {
          fieldId,
          updatesKeys: Object.keys(updates),
          hasPicklistValues: 'picklistValues' in updates,
          picklistValuesCount: (updates as any).picklistValues?.length
        });

        // Update field using mockDataManager
        const updatedField = persistedObjectFields.update(fieldId, {
          ...updates,
          lastModified: new Date().toISOString()
        });

        if (!updatedField) {
          throw new Error('Field not found');
        }

        // DEBUG: Log the result
        console.log('[useUpdateField] Updated field result:', {
          fieldId: updatedField.id,
          hasPicklistValues: 'picklistValues' in updatedField,
          picklistValuesCount: updatedField.picklistValues?.length
        });

        return updatedField;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: fieldKeys.byObject(data.objectId) });
      queryClient.invalidateQueries({ queryKey: fieldKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      queryClient.refetchQueries({ queryKey: fieldKeys.byObject(data.objectId) });
    },
  });
}

/**
 * Delete field (soft delete - mark as inactive)
 * Future: Replace with API call to DELETE /api/fields/:fieldId
 */
export function useDeleteField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fieldId, objectId, objectKey }: { fieldId: string; objectId?: string; objectKey?: string }) => {
      const numericId = parseInt(fieldId.replace(/^field_/, ''), 10);
      const isGobodoId = !isNaN(numericId) && String(numericId) === fieldId.replace(/^field_/, '').trim();

      if (USE_GOBODO_API && isGobodoId) {
        let resolvedObjectId: string | undefined = objectId;
        if (!resolvedObjectId) {
          const cachedField = queryClient.getQueryData<CustomField>(fieldKeys.detail(fieldId));
          if (cachedField?.objectId) resolvedObjectId = cachedField.objectId;
        }
        const resultObjectId = await deleteGobodoField(numericId, resolvedObjectId);
        return { fieldId, objectId: resultObjectId };
      }

      {
        // Fallback to mock data
        await new Promise(resolve => setTimeout(resolve, 400));

        // Find field first
        const allFields = persistedObjectFields.get();
        const field = allFields.find(f => f.id === fieldId);
        
        // Use provided objectId or fall back to field's objectId
        const resolvedObjectId = objectId || field?.objectId;

        if (!field) {
          throw new Error('Field not found');
        }

        if (field.isStandard) {
          throw new Error('Cannot delete standard fields');
        }

        const removed = persistedObjectFields.remove(fieldId);
        if (!removed) {
          throw new Error('Failed to delete field');
        }
        return { fieldId, objectId: resolvedObjectId || field.objectId };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: fieldKeys.byObject(data.objectId) });
      queryClient.invalidateQueries({ queryKey: ['objects', 'detail', data.objectId] });
      queryClient.invalidateQueries({ queryKey: ['setup', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get field type display name
 */
export function getFieldTypeLabel(type: CustomField['fieldType']): string {
  const labels: Record<CustomField['fieldType'], string> = {
    text: 'Text',
    textarea: 'Text Area',
    longtextarea: 'Long Text Area',
    richtextarea: 'Rich Text Area',
    number: 'Number',
    currency: 'Currency',
    percent: 'Percent',
    autonumber: 'Auto Number',
    date: 'Date',
    datetime: 'Date/Time',
    time: 'Time',
    checkbox: 'Checkbox',
    badge: 'Badge',
    picklist: 'Picklist',
    multipicklist: 'Multi-Select Picklist',
    lookup: 'Lookup Relationship',
    masterdetail: 'Master-Detail Relationship',
    formula: 'Formula',
    rollup: 'Roll-Up Summary',
    email: 'Email',
    phone: 'Phone',
    url: 'URL',
    geolocation: 'Geolocation',
    file: 'File Upload',
    image: 'Image'
  };
  return labels[type];
}

/**
 * Get available field types for creation
 */
export function getAvailableFieldTypes() {
  return [
    // Basic Text
    {
      value: 'text',
      label: 'Text',
      description: 'Single line of text (up to 255 characters)',
      category: 'text'
    },
    {
      value: 'textarea',
      label: 'Text Area',
      description: 'Multiple lines of text (up to 1,000 characters)',
      category: 'text'
    },
    {
      value: 'longtextarea',
      label: 'Long Text Area',
      description: 'Very long text (up to 32,000 characters)',
      category: 'text'
    },
    {
      value: 'richtextarea',
      label: 'Rich Text Area',
      description: 'Formatted text with bold, italic, links',
      category: 'text'
    },

    // Numbers
    {
      value: 'number',
      label: 'Number',
      description: 'Numeric value',
      category: 'number'
    },
    {
      value: 'currency',
      label: 'Currency',
      description: 'Money amount',
      category: 'number'
    },
    {
      value: 'percent',
      label: 'Percent',
      description: 'Percentage value (0-100)',
      category: 'number'
    },
    {
      value: 'autonumber',
      label: 'Auto Number',
      description: 'Sequential numbering (e.g., INV-0001)',
      category: 'number'
    },

    // Date & Time
    {
      value: 'date',
      label: 'Date',
      description: 'Date picker',
      category: 'datetime'
    },
    {
      value: 'datetime',
      label: 'Date/Time',
      description: 'Date and time',
      category: 'datetime'
    },
    {
      value: 'time',
      label: 'Time',
      description: 'Time only (HH:MM)',
      category: 'datetime'
    },

    // Boolean
    {
      value: 'checkbox',
      label: 'Checkbox',
      description: 'True/false value',
      category: 'other'
    },

    // Picklists
    {
      value: 'picklist',
      label: 'Picklist',
      description: 'Dropdown list with single selection',
      category: 'picklist'
    },
    {
      value: 'multipicklist',
      label: 'Multi-Select Picklist',
      description: 'Multiple selections from list',
      category: 'picklist'
    },

    // Relationships
    {
      value: 'lookup',
      label: 'Lookup Relationship',
      description: 'Link to another record (loosely coupled)',
      category: 'relationship'
    },
    {
      value: 'masterdetail',
      label: 'Master-Detail Relationship',
      description: 'Parent-child relationship (tightly coupled)',
      category: 'relationship'
    },

    // Calculated Fields
    {
      value: 'formula',
      label: 'Formula',
      description: 'Calculated field based on other fields',
      category: 'calculated'
    },
    {
      value: 'rollup',
      label: 'Roll-Up Summary',
      description: 'Aggregate data from child records',
      category: 'calculated'
    },

    // Contact Information
    {
      value: 'email',
      label: 'Email',
      description: 'Email address',
      category: 'contact'
    },
    {
      value: 'phone',
      label: 'Phone',
      description: 'Phone number',
      category: 'contact'
    },
    {
      value: 'url',
      label: 'URL',
      description: 'Website link',
      category: 'contact'
    },

    // Location & Advanced
    {
      value: 'geolocation',
      label: 'Geolocation',
      description: 'Latitude and longitude coordinates',
      category: 'location'
    },
    {
      value: 'file',
      label: 'File Upload',
      description: 'Attach files to records',
      category: 'advanced',
      badge: 'Soon'
    },
    {
      value: 'image',
      label: 'Image',
      description: 'Display images',
      category: 'advanced',
      badge: 'Soon'
    }
  ];
}
